import { Divider, Stack } from '@mui/material';
import { isBefore } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Status, useSelectedOrganisation, Widget } from '../../../shared';
import { ISupportCaseInfo } from '../../models';
import {
    useWarningPanelAppointments,
    useWarningPanelLastVisits,
    useWarningPanelPostalCode,
    useWarningPanelSupportPeriods,
} from '../../utils/warning-panel-info.util';

interface Props {
    supportCase: ISupportCaseInfo;
}

export const RegistrationWarningPanel: FC<Props> = ({ supportCase }) => {
    const { t } = useTranslation();
    const { organisation } = useSelectedOrganisation();

    const activeSupportPeriods = supportCase.activeSupportPeriods.filter(
        ({ startDate, supportTypesInfo }) =>
            isBefore(startDate, new Date()) &&
            supportTypesInfo.some((info) => organisation?.supportTypes.some(({ id }) => id === info.supportTypeId)),
    );

    const postalCode = useWarningPanelPostalCode(t, supportCase, organisation);
    const supportPeriods = useWarningPanelSupportPeriods(t, supportCase, activeSupportPeriods);
    const appointments = useWarningPanelAppointments(t, activeSupportPeriods);
    const lastVisits = useWarningPanelLastVisits(t, supportCase);

    return (
        <Widget>
            <Stack spacing={1}>
                <Status label={t('postalCodeLabel')} value={postalCode.value} status={postalCode.status} />

                <Divider />

                {supportPeriods.map((supportPeriod) => (
                    <Status
                        label={t('supportPeriodWithSupportTypes', { supportType: supportPeriod.supportType })}
                        value={supportPeriod.value}
                        status={supportPeriod.status}
                    />
                ))}

                <Divider />

                {appointments?.length > 0 && (
                    <Stack spacing={1}>
                        {appointments.map((appointment) => (
                            <Status
                                label={t('appointmentWithSupportTypes', { supportType: appointment.supportType })}
                                value={appointment.value}
                                status={appointment.status}
                            />
                        ))}

                        <Divider />
                    </Stack>
                )}

                {lastVisits.map(({ supportType, value, status }) => (
                    <Status label={t('lastVisit', { supportType })} value={value} status={status} />
                ))}
            </Stack>
        </Widget>
    );
};
